import React, { useState } from 'react';
import { 
  Wallet, 
  Search, 
  ArrowDownRight, 
  CheckCircle2, 
  RotateCcw, 
  AlertTriangle, 
  Eye, 
  Coins, 
  TrendingUp, 
  FileText,
  Clock,
  Radio,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { EscrowOrder, WebhookRecord, AdSubscription } from '../types/admin';

export const EscrowLedgerView: React.FC = () => {
  const [subTab, setSubTab] = useState<'escrows' | 'webhooks' | 'ads'>('escrows');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Inspector & override states
  const [inspectingOrder, setInspectingOrder] = useState<EscrowOrder | null>(null);
  const [overrideAction, setOverrideAction] = useState<{ order: EscrowOrder; type: 'settle' | 'refund' } | null>(null);
  const [overrideLoading, setOverrideLoading] = useState(false);

  // Sample real-time escrow orders with exact 8.0625% formula
  const [orders, setOrders] = useState<EscrowOrder[]>([
    {
      id: 'ord_escrow_001',
      orderNumber: 'ORD-9842',
      buyerId: 'usr_003_chioma',
      buyerName: 'Chioma Eze',
      sellerId: 'usr_002_tunde',
      sellerName: 'Tunde Bakare',
      listingId: 'lst_phone_092',
      itemTitle: 'iPhone 12 Pro 128GB (Pacific Blue)',
      itemPrice: 350000,
      escrowBaseFee: 4375, // 1.25% tier
      platformFee: 26250, // 7.5%
      vatOnFee: 1969, // 7.5% on 26250
      totalDeduction: 28219, // 8.0625%
      netVendorPayout: 321781,
      status: 'held',
      disputeActive: false,
      courier: 'Pending Pickup',
      createdAt: 'Today, 11:20 AM'
    },
    {
      id: 'ord_escrow_002',
      orderNumber: 'ORD-9839',
      buyerId: 'usr_001_alex',
      buyerName: 'Alex Okafor',
      sellerId: 'usr_002_tunde',
      sellerName: 'Tunde Bakare',
      listingId: 'lst_furn_110',
      itemTitle: 'Vintage Mid-Century Teak Armchair',
      itemPrice: 65000,
      escrowBaseFee: 1625, // 2.5% tier
      platformFee: 4875, // 7.5%
      vatOnFee: 366, // 7.5% of 4875
      totalDeduction: 5241, // 8.0625%
      netVendorPayout: 59759,
      status: 'dispatched',
      disputeActive: false,
      courier: 'Gokada Instant Dispatch',
      trackingCode: 'GK-894129',
      riderPhone: '+234 810 555 3322',
      createdAt: 'Yesterday, 03:45 PM',
      dispatchedAt: 'Today, 08:30 AM'
    },
    {
      id: 'ord_escrow_003',
      orderNumber: 'ORD-9830',
      buyerId: 'usr_006_kemi',
      buyerName: 'Kemi Adeleke',
      sellerId: 'usr_004_fatima',
      sellerName: 'Fatima Sani',
      listingId: 'lst_lamp_012',
      itemTitle: 'Handcrafted Brass Table Lamp',
      itemPrice: 18000,
      escrowBaseFee: 585, // 3.25% tier (min 500)
      platformFee: 1350, // 7.5%
      vatOnFee: 101, // 7.5% of 1350
      totalDeduction: 1451, // 8.0625%
      netVendorPayout: 16549,
      status: 'delivered',
      disputeActive: false,
      courier: 'GIG Logistics',
      trackingCode: 'GIG-449102',
      createdAt: '2 days ago',
      deliveredAt: '2026-09-16 10:15 AM'
    },
    {
      id: 'ord_escrow_004',
      orderNumber: 'ORD-9815',
      buyerId: 'usr_007_chidi',
      buyerName: 'Chidi Nnamdi',
      sellerId: 'usr_001_alex',
      sellerName: 'Alex Okafor',
      listingId: 'lst_macbook_042',
      itemTitle: 'MacBook Air M1 256GB Silver',
      itemPrice: 480000,
      escrowBaseFee: 6000, // 1.25% tier
      platformFee: 36000, // 7.5%
      vatOnFee: 2700, // 7.5% of 36000
      totalDeduction: 38700, // 8.0625%
      netVendorPayout: 441300,
      status: 'settled',
      disputeActive: false,
      courier: 'In-Person Safe Exchange',
      createdAt: '4 days ago',
      settledAt: 'Yesterday, 02:00 PM',
      autoReleased: true
    }
  ]);

  // Sample Flutterwave webhook records
  const [webhooks] = useState<WebhookRecord[]>([
    {
      id: 'wh_flw_001',
      txRef: 'FLW-TX-9842-chioma',
      flwId: '4892104',
      userId: 'usr_003_chioma',
      customerEmail: 'chioma.eze@hotmail.com',
      amount: 350000,
      status: 'successful',
      channel: 'card',
      processedAt: 'Today, 11:20:15 AM',
      verified: true
    },
    {
      id: 'wh_flw_002',
      txRef: 'FLW-TX-9839-alex',
      flwId: '4891980',
      userId: 'usr_001_alex',
      customerEmail: 'alex.okafor@gmail.com',
      amount: 65000,
      status: 'successful',
      channel: 'bank_transfer',
      processedAt: 'Yesterday, 03:45:22 PM',
      verified: true
    },
    {
      id: 'wh_flw_003',
      txRef: 'FLW-TX-9830-kemi',
      flwId: '4889212',
      userId: 'usr_006_kemi',
      customerEmail: 'kemi.adeleke@gmail.com',
      amount: 18000,
      status: 'successful',
      channel: 'ussd',
      processedAt: '2 days ago',
      verified: true
    }
  ]);

  // Sample vendor ad subscriptions
  const [adSubs] = useState<AdSubscription[]>([
    {
      id: 'ad_sub_101',
      vendorId: 'usr_002_tunde',
      vendorName: 'Tunde Bakare',
      listingId: 'lst_furn_110',
      listingTitle: 'Vintage Mid-Century Teak Armchair',
      amountPaid: 7500,
      durationDays: 5,
      startDate: '2026-09-14',
      endDate: '2026-09-19',
      status: 'active'
    },
    {
      id: 'ad_sub_102',
      vendorId: 'usr_004_fatima',
      vendorName: 'Fatima Sani',
      listingId: 'lst_lamp_012',
      listingTitle: 'Handcrafted Brass Table Lamp',
      amountPaid: 3000,
      durationDays: 2,
      startDate: '2026-09-15',
      endDate: '2026-09-17',
      status: 'active'
    }
  ]);

  const handleExecuteOverride = async () => {
    if (!overrideAction) return;
    setOverrideLoading(true);
    try {
      await new Promise(r => setTimeout(r, 600));
      const targetStatus = overrideAction.type === 'settle' ? 'settled' : 'refunded';
      setOrders(prev => prev.map(o => {
        if (o.id === overrideAction.order.id) {
          return { ...o, status: targetStatus };
        }
        return o;
      }));
      setOverrideAction(null);
    } finally {
      setOverrideLoading(false);
    }
  };

  const filteredOrders = orders.filter(o => {
    const matchSearch = o.orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        o.itemTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        o.buyerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        o.sellerName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      {/* Sub Tabs */}
      <div className="flex items-center justify-between border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] pb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSubTab('escrows')}
            className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all
              ${subTab === 'escrows'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            Escrow Monitor ({orders.length})
          </button>

          <button
            onClick={() => setSubTab('webhooks')}
            className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all
              ${subTab === 'webhooks'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            Flutterwave Webhook Logs ({webhooks.length})
          </button>

          <button
            onClick={() => setSubTab('ads')}
            className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all
              ${subTab === 'ads'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            Vendor Ad Subscriptions ({adSubs.length})
          </button>
        </div>
      </div>

      {subTab === 'escrows' && (
        <div className="space-y-4">
          {/* Filters Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl border
            bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
            light:bg-white light:border-[#D5E4DC]
          ">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by order #, item, or party..."
                className="w-full pl-9 pr-4 py-2 rounded-xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <span className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 rounded-xl text-xs font-medium border outline-none
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                "
              >
                <option value="all">All Stages</option>
                <option value="held">Payment in Escrow</option>
                <option value="dispatched">Dispatched</option>
                <option value="delivered">Delivered (48-hr Inspection)</option>
                <option value="settled">Settled</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>

          {/* Escrow Orders Table */}
          <div className="rounded-3xl border overflow-hidden transition-colors
            bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
            light:bg-white light:border-[#D5E4DC] light:shadow-sm
          ">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider font-semibold">
                    <th className="py-4 px-6">Order ID & Item</th>
                    <th className="py-4 px-6">Buyer & Seller</th>
                    <th className="py-4 px-6">Gross Amount</th>
                    <th className="py-4 px-6">Deduction (8.0625%)</th>
                    <th className="py-4 px-6">Net Vendor Payout</th>
                    <th className="py-4 px-6">Stage</th>
                    <th className="py-4 px-6 text-right">Admin Overrides</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-[#1a382e]/50 dark:hover:bg-[#1a382e]/50 light:hover:bg-gray-50/80 transition-colors">
                      <td className="py-4 px-6">
                        <div className="font-mono text-xs font-bold text-brand-400">
                          {order.orderNumber}
                        </div>
                        <div className="font-bold text-sm text-white dark:text-white light:text-[#0D1C17] max-w-xs truncate" title={order.itemTitle}>
                          {order.itemTitle}
                        </div>
                        <div className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                          {order.createdAt}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-white dark:text-white light:text-[#0D1C17] font-semibold">
                          Buyer: {order.buyerName}
                        </div>
                        <div className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                          Vendor: {order.sellerName}
                        </div>
                      </td>
                      <td className="py-4 px-6 font-extrabold text-sm text-white dark:text-white light:text-[#0D1C17]">
                        ₦{order.itemPrice.toLocaleString()}
                      </td>
                      <td className="py-4 px-6 text-amber-400 font-semibold">
                        -₦{order.totalDeduction.toLocaleString()}
                      </td>
                      <td className="py-4 px-6 text-brand-400 font-bold text-sm">
                        ₦{order.netVendorPayout.toLocaleString()}
                      </td>
                      <td className="py-4 px-6">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setInspectingOrder(order)}
                            title="Inspect Exact Fee Breakdown"
                            className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] light:hover:bg-gray-100"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {order.status !== 'settled' && order.status !== 'refunded' && (
                            <>
                              <button
                                onClick={() => setOverrideAction({ order, type: 'settle' })}
                                title="Force Settle Escrow to Vendor"
                                className="px-2.5 py-1 rounded-full text-[11px] font-bold text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 transition-colors"
                              >
                                Release
                              </button>
                              <button
                                onClick={() => setOverrideAction({ order, type: 'refund' })}
                                title="Force Refund Escrow to Buyer"
                                className="px-2.5 py-1 rounded-full text-[11px] font-bold text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 transition-colors"
                              >
                                Refund
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {subTab === 'webhooks' && (
        /* FLUTTERWAVE WEBHOOK LOGS */
        <div className="rounded-3xl border overflow-hidden transition-colors
          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
          light:bg-white light:border-[#D5E4DC] light:shadow-sm
        ">
          <div className="p-6 border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] flex items-center justify-between">
            <div>
              <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                HMAC SHA-512 Inbound Webhook Trail
              </h4>
              <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                Server-side listener (/api/flw-webhook) verifying verif-hash and wallet funding idempotency
              </p>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-brand-500/15 text-brand-400 border border-brand-500/30">
              Idempotency: Active
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider font-semibold">
                  <th className="py-4 px-6">Transaction Ref (tx_ref)</th>
                  <th className="py-4 px-6">Customer Email</th>
                  <th className="py-4 px-6">Channel</th>
                  <th className="py-4 px-6">Amount Funded</th>
                  <th className="py-4 px-6">Timestamp</th>
                  <th className="py-4 px-6">Signature Verification</th>
                  <th className="py-4 px-6 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
                {webhooks.map((wh) => (
                  <tr key={wh.id} className="hover:bg-[#1a382e]/50 dark:hover:bg-[#1a382e]/50 light:hover:bg-gray-50/80 transition-colors">
                    <td className="py-4 px-6 font-mono font-bold text-brand-400">
                      {wh.txRef}
                    </td>
                    <td className="py-4 px-6 text-white dark:text-white light:text-[#0D1C17] font-medium">
                      {wh.customerEmail}
                    </td>
                    <td className="py-4 px-6 uppercase text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      {wh.channel}
                    </td>
                    <td className="py-4 px-6 font-bold text-white dark:text-white light:text-[#0D1C17]">
                      ₦{wh.amount.toLocaleString()}
                    </td>
                    <td className="py-4 px-6 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      {wh.processedAt}
                    </td>
                    <td className="py-4 px-6">
                      <span className="flex items-center gap-1.5 text-brand-400 font-semibold">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        HMAC Verified
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <StatusBadge status={wh.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subTab === 'ads' && (
        /* VENDOR AD SUBSCRIPTIONS */
        <div className="rounded-3xl border overflow-hidden transition-colors
          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
          light:bg-white light:border-[#D5E4DC] light:shadow-sm
        ">
          <div className="p-6 border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] flex items-center justify-between">
            <div>
              <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                Active Vendor Ad Campaigns
              </h4>
              <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                Vendor boosted listings (₦3,000+ per 2 to 30 days) with in-app banner placement
              </p>
            </div>
            <span className="text-xs text-brand-400 font-bold">
              Total Revenue: ₦10,500
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider font-semibold">
                  <th className="py-4 px-6">Campaign ID & Listing</th>
                  <th className="py-4 px-6">Vendor Name</th>
                  <th className="py-4 px-6">Duration</th>
                  <th className="py-4 px-6">Amount Paid</th>
                  <th className="py-4 px-6">Validity Window</th>
                  <th className="py-4 px-6 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
                {adSubs.map((ad) => (
                  <tr key={ad.id} className="hover:bg-[#1a382e]/50 dark:hover:bg-[#1a382e]/50 light:hover:bg-gray-50/80 transition-colors">
                    <td className="py-4 px-6">
                      <div className="font-mono text-xs text-brand-400 font-semibold">{ad.id}</div>
                      <div className="font-bold text-white dark:text-white light:text-[#0D1C17]">{ad.listingTitle}</div>
                    </td>
                    <td className="py-4 px-6 font-medium text-white dark:text-white light:text-[#0D1C17]">
                      {ad.vendorName}
                    </td>
                    <td className="py-4 px-6">
                      <span className="font-bold text-brand-400">{ad.durationDays} Days</span>
                    </td>
                    <td className="py-4 px-6 font-bold text-white dark:text-white light:text-[#0D1C17]">
                      ₦{ad.amountPaid.toLocaleString()}
                    </td>
                    <td className="py-4 px-6 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      {ad.startDate} → {ad.endDate}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <StatusBadge status={ad.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fee Breakdown Inspector Modal */}
      {inspectingOrder && (
        <Modal
          isOpen={Boolean(inspectingOrder)}
          onClose={() => setInspectingOrder(null)}
          title={`Financial Settlement Breakdown: ${inspectingOrder.orderNumber}`}
          subtitle="Strict adherence to 7.5% platform fee + 7.5% VAT on fee (8.0625% net deduction)"
          maxWidth="lg"
        >
          <div className="space-y-4 text-xs">
            <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c] light:border-gray-200 space-y-3">
              <div className="flex justify-between items-center text-sm font-bold text-white dark:text-white light:text-gray-900 pb-2 border-b border-[#22493c] dark:border-[#22493c] light:border-gray-200">
                <span>Gross Order Value:</span>
                <span>₦{inspectingOrder.itemPrice.toLocaleString()}</span>
              </div>

              <div className="flex justify-between items-center text-[#90cbb7] dark:text-[#90cbb7] light:text-gray-600">
                <span>Escrow Base Service Fee (min ₦500):</span>
                <span>₦{inspectingOrder.escrowBaseFee.toLocaleString()}</span>
              </div>

              <div className="flex justify-between items-center text-[#90cbb7] dark:text-[#90cbb7] light:text-gray-600">
                <span>Platform Fee (7.5%):</span>
                <span>₦{inspectingOrder.platformFee.toLocaleString()}</span>
              </div>

              <div className="flex justify-between items-center text-[#90cbb7] dark:text-[#90cbb7] light:text-gray-600">
                <span>VAT on Platform Fee (7.5% of 7.5%):</span>
                <span>₦{inspectingOrder.vatOnFee.toLocaleString()}</span>
              </div>

              <div className="flex justify-between items-center text-amber-400 font-semibold pt-2 border-t border-[#22493c] dark:border-[#22493c] light:border-gray-200">
                <span>Total Vendor Payout Deductions (8.0625%):</span>
                <span>-₦{inspectingOrder.totalDeduction.toLocaleString()}</span>
              </div>

              <div className="flex justify-between items-center text-base font-extrabold text-brand-400 pt-2 border-t border-[#22493c] dark:border-[#22493c] light:border-gray-200">
                <span>Net Vendor Wallet Credit:</span>
                <span>₦{inspectingOrder.netVendorPayout.toLocaleString()}</span>
              </div>
            </div>

            <div className="space-y-1.5 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
              <p>• Buyer: {inspectingOrder.buyerName}</p>
              <p>• Vendor: {inspectingOrder.sellerName}</p>
              <p>• Courier Logistics: {inspectingOrder.courier} ({inspectingOrder.trackingCode || 'N/A'})</p>
              <p>• Current Escrow Stage: <strong className="uppercase text-white dark:text-white light:text-gray-900">{inspectingOrder.status}</strong></p>
            </div>

            <div className="pt-4 border-t border-[#22493c] dark:border-[#22493c] flex justify-end">
              <button
                onClick={() => setInspectingOrder(null)}
                className="px-5 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirmation Dialog for Overrides */}
      {overrideAction && (
        <ConfirmDialog
          isOpen={Boolean(overrideAction)}
          onClose={() => setOverrideAction(null)}
          onConfirm={handleExecuteOverride}
          title={overrideAction.type === 'settle' ? `Force Release Escrow to Vendor?` : `Force Refund Escrow to Buyer?`}
          description={
            overrideAction.type === 'settle'
              ? `You are manually overriding escrow for ${overrideAction.order.orderNumber}. Net payout ₦${overrideAction.order.netVendorPayout.toLocaleString()} will be immediately credited to ${overrideAction.order.sellerName}'s wallet, deducting the 8.0625% platform fee.`
              : `You are issuing a 100% full refund for ${overrideAction.order.orderNumber}. ₦${overrideAction.order.itemPrice.toLocaleString()} will be returned immediately to ${overrideAction.order.buyerName}'s wallet.`
          }
          confirmButtonText={overrideAction.type === 'settle' ? 'Release Escrow Payout' : 'Refund Escrow Payment'}
          variant={overrideAction.type === 'settle' ? 'primary' : 'danger'}
          loading={overrideLoading}
        />
      )}
    </div>
  );
};
